/**
 * Invoice detail (design D): the document as the customer sees it, its
 * balance, payments and activity, and every action the current status
 * allows (detailModel.availableActions). After any action the document is
 * fetched again, so the figures on screen are always the server's.
 *
 * `?action=pay` / `?action=void` open that dialog straight away (used by
 * the list's row menu), then the param is dropped so a reload doesn't.
 *
 * Quotations (design H) share the page: "Valid until" in the header, the
 * customer's response, the deposit asked for on acceptance (recorded with
 * the same payment dialog, capped at the unpaid deposit) and Convert to
 * invoice, which opens the new invoice draft in the editor.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import InvoiceRepository, { DocumentDetail, InvoiceDoc, MessageRow } from '../../../internals/repository/InvoiceRepository';
import { useInvoiceSettings, useSelectedShop } from '../useInvoicing';
import { date, dateTime, dueHint, money } from '../format';
import { depositView, expiryHint, quoteActions, responseCard } from '../quoteModel';
import {
  availableActions, balanceRows, errorText, fieldProblems, moneyRows, paidPercent,
} from '../detailModel';
import StatusChip from '../components/StatusChip';
import InvoicePreview from '../components/InvoicePreview';
import ActivityTimeline from '../components/ActivityTimeline';
import RecordPaymentModal from '../components/RecordPaymentModal';
import CreditNoteModal from '../components/CreditNoteModal';
import VoidModal, { Dialog, ErrorBanner } from '../components/VoidModal';
import ClaimPanel from '../components/ClaimPanel';
import ShareDialog from '../components/ShareDialog';
import NextReminderCard from '../components/NextReminderCard';
import MakeRecurringModal from '../components/MakeRecurringModal';
import DeliveryTag, { ChannelGlyph, DeliveryTags } from '../components/DeliveryTag';
import {
  canRetry, creditText, deliveryTags, messageTag, messageTime, offersDeviceShare, retryProblem, templateLabel,
} from '../deliveryModel';

type Modal = null | 'pay' | 'credit' | 'void' | 'regen' | 'delete' | 'share' | 'send' | 'recurring' | { link: string };
interface Problem { text: string; fields: string[]; offerEdit?: boolean }

const capsCls = 'text-[11px] font-semibold uppercase tracking-[.08em] text-text3';

export default function InvoiceDetailView() {
  const { docId = '' } = useParams();
  const navigate = useNavigate();
  const [search, setSearch] = useSearchParams();
  const shop = useSelectedShop();
  const shopId: string = shop?.shopId ?? '';
  const { settings } = useInvoiceSettings(shopId || undefined);

  const [detail, setDetail] = useState<DocumentDetail | null>(null);
  const [loadError, setLoadError] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<Modal>(null);
  const [problem, setProblem] = useState<Problem | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const flash = useCallback((text: string) => {
    setToast(text);
    window.setTimeout(() => setToast((t) => (t === text ? null : t)), 3000);
  }, []);

  const load = useCallback(async (quiet = false) => {
    if (!shopId || !docId) return;
    if (!quiet) setLoading(true);
    try {
      const d = await InvoiceRepository.get(shopId, docId);
      setDetail(d);
      setLoadError(null);
    } catch (e) {
      setLoadError(e);
      if (!quiet) setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [shopId, docId]);

  useEffect(() => { load(); }, [load]);

  const doc = detail?.document;
  const actions = useMemo(() => (doc ? availableActions(doc) : new Set()), [doc]);

  // Deep links from the list: open the dialog once the document is here.
  useEffect(() => {
    const action = search.get('action');
    if (!action || !doc) return;
    if (action === 'pay' && (actions.has('recordPayment') || (doc.docType === 'QUOTATION' && quoteActions(doc as any).has('recordDeposit')))) setModal('pay');
    if (action === 'void' && actions.has('void')) setModal('void');
    const next = new URLSearchParams(search);
    next.delete('action');
    setSearch(next, { replace: true });
  }, [search, doc, actions, setSearch]);

  // A4 preview scaled to the width of its column.
  const canvasRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return undefined;
    const fit = () => setScale(Math.min(1, Math.max(0.3, (el.clientWidth - 48) / 595)));
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [doc?.docId]);

  // More menu: Escape or a click outside closes it.
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDown = (e: MouseEvent) => { if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [menuOpen]);

  const run = async (name: string, fn: () => Promise<void>, overrides: Record<string, string> = {}, offerEdit = false) => {
    setMenuOpen(false);
    setBusy(name);
    setProblem(null);
    try {
      await fn();
    } catch (e: any) {
      setProblem({ text: errorText(e, overrides), fields: fieldProblems(e?.fields), offerEdit });
    } finally {
      setBusy(null);
    }
  };

  const convert = () => run('convert', async () => {
    const res = await InvoiceRepository.convert(shopId, docId, false);
    navigate(`/invoices/${encodeURIComponent(res.document.docId)}/edit`);
  }, { invalid_state: 'This quotation can no longer be converted. Reload to see its current state.' });

  const issue = () => run('issue', async () => {
    const res = await InvoiceRepository.issue(shopId, docId);
    await load(true);
    flash(`Issued as ${res.document.number}`);
  }, {}, true);

  const removeDraft = () => run('delete', async () => {
    setModal(null);
    await InvoiceRepository.remove(shopId, docId);
    navigate('/invoices');
  });

  const duplicate = () => run('duplicate', async () => {
    const res = await InvoiceRepository.duplicate(shopId, docId);
    navigate(`/invoices/${encodeURIComponent(res.document.docId)}/edit`);
  });

  const pdf = (size: 'A4' | '80mm', copy: 'ORIGINAL' | 'DUPLICATE') => {
    // Open the tab inside the click so pop-up blockers allow it, then point it at the signed URL.
    const win = window.open('', '_blank');
    return run('pdf', async () => {
      try {
        const { url } = await InvoiceRepository.pdfUrl(shopId, docId, size, copy);
        if (win) { win.opener = null; win.location.href = url; } else window.open(url, '_blank', 'noopener');
      } catch (e) {
        win?.close();
        throw e;
      }
    }, { invalid_state: 'A PDF is made when the document is issued.' });
  };

  const regenerate = () => run('regen', async () => {
    setModal(null);
    const res = await InvoiceRepository.regenerateLink(shopId, docId);
    setModal({ link: res.shareUrl });
    await load(true);
  });

  const copyLink = async (url?: string) => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      flash('Link copied');
    } catch {
      setProblem({ text: `Copy did not work. The link is ${url}`, fields: [] });
    }
  };


  const afterModal = async (text: string) => {
    setModal(null);
    await load(true);
    flash(text);
  };

  // ── States ────────────────────────────────────────────────────────────────
  const shell = 'h-screen flex flex-col w-full p-5 md:px-8 md:py-7 overflow-auto [&>*]:shrink-0 gap-5';

  if (!shopId) {
    return <div className={shell}><p className="text-text2">Select a shop to see its invoices.</p></div>;
  }
  if (loading && !detail) {
    return (
      <div className={shell} aria-busy="true" aria-label="Loading invoice">
        <div className="h-4 w-28 animate-pulse rounded bg-surface3" />
        <div className="h-8 w-64 animate-pulse rounded bg-surface3" />
        <div className="grid grid-cols-1 gap-5 min-[1180px]:grid-cols-[minmax(0,1fr)_380px]">
          <div className="h-[600px] animate-pulse rounded-2xl bg-surface3" />
          <div className="flex flex-col gap-3.5">
            {[140, 120, 220].map((h) => <div key={h} className="animate-pulse rounded-2xl bg-surface3" style={{ height: h }} />)}
          </div>
        </div>
      </div>
    );
  }
  if (!detail || !doc) {
    const notFound = loadError?.code === 'not_found' || loadError?.status === 404;
    return (
      <div className={shell}>
        <Link to="/invoices" className="text-[13px] text-text3 hover:text-brand">← All invoices</Link>
        <div className="nl-card flex flex-col items-center gap-3 px-6 py-16 text-center">
          <h1 className="m-0 text-[18px] font-semibold text-text1">{notFound ? 'Invoice not found' : 'This invoice could not load'}</h1>
          <p className="m-0 max-w-md text-[14px] text-text2">
            {notFound ? 'This invoice does not exist or belongs to another shop.' : errorText(loadError)}
          </p>
          {notFound
            ? <Link to="/invoices" className="nl-btn nl-btn--secondary">Back to invoices</Link>
            : <button type="button" className="nl-btn nl-btn--secondary" onClick={() => load()}>Try again</button>}
        </div>
      </div>
    );
  }

  // ── Document ──────────────────────────────────────────────────────────────
  const cur = doc.currency;
  const isQuote = doc.docType === 'QUOTATION';
  const isInvoice = doc.docType === 'INVOICE';
  const isDraft = doc.status === 'DRAFT';
  const isVoid = doc.status === 'VOID';
  const overdue = doc.displayStatus === 'OVERDUE';
  const hint = isInvoice && !isDraft && !isVoid && doc.displayStatus !== 'PAID' ? dueHint(doc.dueDate) : null;
  const cust = doc.customer || ({} as InvoiceDoc['customer']);
  const custMain = cust.companyName || cust.name || 'No customer';
  // Names in the body font; only the phone number is mono (design type rules).
  const custName = cust.companyName ? cust.name : '';
  const custContact = [cust.phoneE164, cust.email].filter(Boolean).join(' · ');
  const rows = moneyRows(detail);
  const pendingClaims = (detail.payments || []).filter((p) => p.status === 'PENDING_APPROVAL');
  const pct = paidPercent(doc);
  const has = (a: string) => actions.has(a as any);
  const branding = {
    accentColor: settings?.branding?.accentColor,
    footerText: settings?.branding?.footerText,
    bankAccounts: settings?.bankAccounts,
    shopTin: settings?.shopTin,
  };
  const voidedOn = doc.voidedAt ? date(new Date(doc.voidedAt + 330 * 60000).toISOString()) : null;
  const quote = doc as InvoiceDoc & { invoicedDocId?: string; invoicedNumber?: string };
  const qActs = isQuote ? quoteActions(quote) : new Set<string>();
  const qHas = (a: string) => qActs.has(a as any);
  const deposit = isQuote ? depositView(doc, cur) : null;
  const response = isQuote ? responseCard(doc) : null;
  const validHint = isQuote ? expiryHint(doc.validUntil, doc.displayStatus) : null;
  const backTo = isQuote ? '/invoices/quotations' : '/invoices';
  const backLabel = isQuote ? '← Quotations' : '← All invoices';
  // The payment dialog caps at the document's balance; on a quotation that is the unpaid deposit.
  const payDoc = isQuote && deposit ? { ...doc, balanceMinor: deposit.remainingMinor } : doc;

  const menuItem = 'flex w-full items-center rounded-lg border-0 bg-transparent px-2.5 py-2 text-left text-[14px] font-medium text-text1 hover:bg-surface2 disabled:opacity-50';
  // Quotations show Duplicate as a button; their menu holds the PDF and Void.
  const menuDuplicate = has('duplicate') && !isQuote;
  const canVoid = has('void') || qHas('void');
  const showMenu = has('pdf') || menuDuplicate || has('creditNote') || has('regenerateLink') || canVoid;

  return (
    <div className={shell}>
      {/* Header */}
      <header className="flex flex-wrap items-start justify-between gap-5">
        <div className="flex min-w-0 flex-col gap-2">
          <Link to={backTo} className="self-start text-[13px] text-text3 hover:text-brand">{backLabel}</Link>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="m-0 font-mono text-[26px] font-semibold tracking-[-0.02em] text-text1">
              {doc.number ?? (isQuote ? 'Draft quotation' : 'Draft')}
            </h1>
            <StatusChip status={doc.displayStatus} />
            {doc.isTaxInvoice && (
              <span className="inline-flex h-[22px] items-center rounded-md border border-borderc2 px-2 text-[12px] font-semibold text-text2">Tax invoice</span>
            )}
            {doc.revision > 1 && <span className="text-[12px] text-text3">Revision {doc.revision}</span>}
          </div>
          <span className="text-[14px] text-text2">
            <span className="font-semibold text-text1">{custMain}</span>
            {custName && <> · <span>{custName}</span></>}
            {custContact && <> · <span className="nl-num">{custContact}</span></>}
          </span>
          <DeliveryTags tags={deliveryTags(doc as any)} />
          {detail.related.parent && (
            <span className="text-[13px] text-text3">
              From <Link className="nl-num text-brand" to={`/invoices/${encodeURIComponent(detail.related.parent.docId)}`}>{detail.related.parent.number ?? 'draft'}</Link>
            </span>
          )}
        </div>

        <div className="flex flex-col items-end gap-3">
          <dl className="m-0 flex flex-wrap justify-end gap-6">
            <div className="flex flex-col items-end">
              <dt className={capsCls}>Total</dt>
              <dd className="m-0 nl-num text-[17px] font-semibold text-text1">{money(doc.totalMinor, cur)}</dd>
            </div>
            {isInvoice && !isDraft && (
              <div className="flex flex-col items-end">
                <dt className={capsCls}>Balance due</dt>
                <dd
                  className="m-0 nl-num text-[17px] font-semibold"
                  style={{ color: overdue ? 'var(--negFg)' : doc.displayStatus === 'PAID' ? 'var(--posFg)' : 'var(--text)' }}
                >
                  {isVoid ? '—' : money(Math.max(0, doc.balanceMinor), cur)}
                </dd>
              </div>
            )}
            <div className="flex flex-col items-end">
              <dt className={capsCls}>{isQuote ? 'Valid until' : 'Due date'}</dt>
              <dd className="m-0 text-[17px] font-semibold" style={{ color: overdue ? 'var(--negFg)' : 'var(--text)' }}>
                {date(isQuote ? doc.validUntil : doc.dueDate)}
              </dd>
              {hint && (
                <span className="text-[12px] font-semibold" style={{ color: hint.overdue ? 'var(--negFg)' : 'var(--text3)' }}>{hint.text}</span>
              )}
              {validHint && (
                <span className="text-[12px] font-semibold" style={{ color: validHint.expired ? 'var(--negFg)' : 'var(--text3)' }}>{validHint.text}</span>
              )}
            </div>
          </dl>

          <div className="flex flex-wrap justify-end gap-2">
            {!isDraft && (!isQuote || qHas('send')) && (
              <>
                <button type="button" className="nl-btn nl-btn--secondary" onClick={() => setModal('send')}>Send</button>
                <button type="button" className="nl-btn nl-btn--secondary" onClick={() => setModal('share')}>Share</button>
              </>
            )}
            {has('copyLink') && (
              <button type="button" className="nl-btn nl-btn--secondary" onClick={() => copyLink(doc.shareUrl)}>Copy link</button>
            )}
            {isDraft && (
              <>
                <button type="button" className="nl-btn nl-btn--danger-outline" disabled={!!busy} onClick={() => setModal('delete')}>Delete draft</button>
                <Link to={`/invoices/${encodeURIComponent(doc.docId)}/edit`} className="nl-btn nl-btn--secondary">Edit</Link>
                <button type="button" className="nl-btn nl-btn--primary" disabled={!!busy} onClick={issue}>
                  {busy === 'issue' ? 'Issuing…' : isQuote ? 'Issue quotation' : 'Issue invoice'}
                </button>
              </>
            )}
            {!isDraft && has('edit') && (
              <Link
                to={`/invoices/${encodeURIComponent(doc.docId)}/edit`}
                className="nl-btn nl-btn--secondary"
                title="Saving creates a new revision with the same number and link"
              >
                Edit
              </Link>
            )}
            {has('recordPayment') && (
              <button type="button" className="nl-btn nl-btn--primary" onClick={() => setModal('pay')}>Record payment</button>
            )}
            {isQuote && !isDraft && (
              <button type="button" className="nl-btn nl-btn--secondary" disabled={!!busy} onClick={duplicate}>
                {busy === 'duplicate' ? 'Duplicating…' : 'Duplicate'}
              </button>
            )}
            {qHas('openInvoice') && quote.invoicedDocId && (
              <Link to={`/invoices/${encodeURIComponent(quote.invoicedDocId)}`} className="nl-btn nl-btn--soft">
                Open {quote.invoicedNumber || 'invoice'}
              </Link>
            )}
            {qHas('convert') && (
              <button type="button" className="nl-btn nl-btn--primary" disabled={!!busy} onClick={convert}>
                {busy === 'convert' ? 'Converting…' : 'Convert to invoice'}
              </button>
            )}
            {!isDraft && showMenu && (
              <div className="relative" ref={menuRef}>
                <button
                  type="button"
                  className="nl-btn nl-btn--secondary"
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                  aria-label="More actions"
                  disabled={!!busy}
                  onClick={() => setMenuOpen((o) => !o)}
                >
                  {busy && !['issue', 'convert', 'duplicate'].includes(busy) ? 'Working…' : 'More ▾'}
                </button>
                {menuOpen && (
                  <div
                    role="menu"
                    className="absolute right-0 top-[calc(100%+6px)] z-30 flex w-[240px] flex-col rounded-xl border border-borderc bg-surface p-1.5"
                    style={{ boxShadow: 'var(--shadowLg)' }}
                  >
                    {has('pdf') && (
                      <>
                        <span className="px-2.5 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-[.07em] text-text3">Download PDF</span>
                        <button role="menuitem" type="button" className={menuItem} onClick={() => pdf('A4', 'ORIGINAL')}>Original · A4</button>
                        <button role="menuitem" type="button" className={menuItem} onClick={() => pdf('A4', 'DUPLICATE')}>Duplicate · A4</button>
                        <button role="menuitem" type="button" className={menuItem} onClick={() => pdf('80mm', 'ORIGINAL')}>80 mm thermal</button>
                        <div className="my-1 h-px bg-borderc" />
                      </>
                    )}
                    {menuDuplicate && <button role="menuitem" type="button" className={menuItem} onClick={duplicate}>Duplicate</button>}
                    {doc.docType === 'INVOICE' && doc.status !== 'DRAFT' && doc.status !== 'VOID' && (
                      <button role="menuitem" type="button" className={menuItem} onClick={() => { setMenuOpen(false); setModal('recurring'); }}>Make recurring</button>
                    )}
                    {has('creditNote') && (
                      <button role="menuitem" type="button" className={menuItem} onClick={() => { setMenuOpen(false); setModal('credit'); }}>Credit note</button>
                    )}
                    {has('regenerateLink') && (
                      <button role="menuitem" type="button" className={menuItem} onClick={() => { setMenuOpen(false); setModal('regen'); }}>Regenerate link</button>
                    )}
                    {canVoid && (
                      <button
                        role="menuitem"
                        type="button"
                        className={menuItem}
                        style={{ color: 'var(--negFg)' }}
                        onClick={() => { setMenuOpen(false); setModal('void'); }}
                      >
                        {isQuote ? 'Void quotation' : 'Void invoice'}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      {problem && (
        <ErrorBanner text={problem.text}>
          {problem.fields.length > 0 && (
            <ul className="mb-0 mt-1.5 list-disc pl-5">{problem.fields.map((f) => <li key={f}>{f}</li>)}</ul>
          )}
          <div className="mt-2 flex gap-2">
            {problem.offerEdit && (
              <Link to={`/invoices/${encodeURIComponent(doc.docId)}/edit`} className="nl-btn nl-btn--xs nl-btn--secondary">Edit</Link>
            )}
            <button type="button" className="nl-btn nl-btn--xs nl-btn--ghost" onClick={() => setProblem(null)}>Dismiss</button>
          </div>
        </ErrorBanner>
      )}

      {isVoid && (
        <div className="flex gap-3 rounded-xl border border-borderc2 px-4 py-3.5" style={{ background: 'var(--neutralSoft)' }}>
          <span aria-hidden className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-surface text-text2">⊘</span>
          <div className="flex flex-col gap-0.5">
            <span className="font-semibold text-text1">{isQuote ? 'This quotation is void' : 'This invoice is void'}</span>
            <span className="text-[13px] text-text2">
              {[doc.voidReason || 'Voided', voidedOn && `Voided on ${voidedOn}`].filter(Boolean).join(' · ')}
              {' · '}{isQuote ? 'The customer link shows a void notice.' : 'The customer link shows a void notice and reminders have stopped.'}
            </span>
          </div>
        </div>
      )}

      {/* Body */}
      <div className="grid grid-cols-1 items-start gap-5 min-[1180px]:grid-cols-[minmax(0,1fr)_380px]">
        <div ref={canvasRef} className="flex justify-center overflow-hidden rounded-2xl p-6" style={{ background: 'var(--paperCanvas)' }}>
          <InvoicePreview doc={doc as any} branding={branding} shop={shop} scale={scale} />
        </div>

        <div className="flex min-w-0 flex-col gap-3.5">
          {isQuote && response && (
            <section
              className="flex flex-col gap-2 rounded-2xl bg-surface px-[18px] py-4"
              style={{ border: `1.5px solid ${response.tone === 'ok' ? 'var(--pos)' : response.tone === 'danger' ? 'var(--neg)' : 'var(--border)'}` }}
              aria-label="Customer response"
            >
              <span
                className="text-[11px] font-bold uppercase tracking-[.08em]"
                style={{ color: response.tone === 'ok' ? 'var(--posFg)' : response.tone === 'danger' ? 'var(--negFg)' : 'var(--text3)' }}
              >
                Customer response
              </span>
              <span className="text-[16px] font-semibold text-text1">{response.title}</span>
              {response.note && (
                <blockquote className="m-0 rounded-lg bg-surface2 px-3 py-2.5 text-[14px] text-text2">“{response.note}”</blockquote>
              )}
              {response.when && <span className="text-[12px] text-text3">{response.when}</span>}
            </section>
          )}

          {isQuote && deposit && (
            <section className="nl-card flex flex-col gap-2 px-[18px] py-4" aria-label="Deposit">
              <div className="flex items-start justify-between gap-2.5">
                <div className="flex flex-col">
                  <span className="font-semibold text-text1">Deposit requested</span>
                  <span className="text-[13px] text-text2">On acceptance</span>
                </div>
                <span className="nl-num text-right font-bold tabular-nums text-text1">{deposit.label}</span>
              </div>
              {(deposit.paidMinor > 0 || doc.displayStatus === 'Q_ACCEPTED') && (
                <>
                  <div className="flex justify-between gap-2.5 text-[14px]" style={{ color: 'var(--posFg)' }}>
                    <span>Paid</span><span className="nl-num tabular-nums">{money(deposit.paidMinor, cur)}</span>
                  </div>
                  <div className="flex justify-between gap-2.5 text-[14px] font-semibold text-text1">
                    <span>Remaining</span><span className="nl-num tabular-nums">{money(deposit.remainingMinor, cur)}</span>
                  </div>
                </>
              )}
              {qHas('recordDeposit') && (
                <button type="button" className="nl-btn nl-btn--sm nl-btn--secondary mt-1 self-start" onClick={() => setModal('pay')}>
                  Record deposit payment
                </button>
              )}
              {doc.displayStatus === 'Q_INVOICED' && deposit.paidMinor > 0 && (
                <span className="text-[12px] text-text3">Applied to {quote.invoicedNumber || 'the invoice'}.</span>
              )}
            </section>
          )}

          {isQuote && pendingClaims.map((k) => (
            <ClaimPanel
              key={k.paymentId}
              shopId={shopId}
              claim={k}
              balanceMinor={deposit?.remainingMinor ?? 0}
              docNumber={doc.number}
              currency={cur}
              onDecided={async (text) => { await load(true); if (text) flash(text); }}
            />
          ))}

          {isQuote && rows.length > 0 && (
            <section className="nl-card overflow-hidden" aria-labelledby="quo-payments">
              <div className="px-[18px] py-3.5">
                <h2 id="quo-payments" className="m-0 text-[14px] font-semibold text-text1">Deposit payments</h2>
              </div>
              {rows.map((p) => (
                <div key={p.key} className="grid gap-x-2.5 gap-y-0.5 border-t border-borderc px-[18px] py-2.5" style={{ gridTemplateColumns: 'minmax(0,1fr) auto' }}>
                  <span className={`font-semibold ${p.rejected ? 'text-text3' : 'text-text1'}`}>{p.title}{p.refunded ? ' · refunded' : ''}</span>
                  <span
                    className={`nl-num font-semibold tabular-nums ${p.rejected ? 'line-through' : ''}`}
                    style={{ color: p.refunded || p.rejected ? 'var(--text3)' : 'var(--posFg)' }}
                  >
                    {money(p.amountMinor, cur)}
                  </span>
                  <span className="min-w-0 truncate text-[12px] text-text3">
                    {p.rejected ? `Rejected · ${p.rejected}` : [p.paidOn ? date(p.paidOn) : null, p.reference].filter(Boolean).join(' · ')}
                  </span>
                  <span className="nl-num text-right text-[12px] text-text3">{p.number ?? ''}</span>
                </div>
              ))}
            </section>
          )}

          {isInvoice && !isDraft && pendingClaims.map((k) => (
            <ClaimPanel
              key={k.paymentId}
              shopId={shopId}
              claim={k}
              balanceMinor={Math.max(0, doc.balanceMinor)}
              docNumber={doc.number}
              currency={cur}
              onDecided={async (text) => { await load(true); if (text) flash(text); }}
            />
          ))}

          {isInvoice && !isDraft && (
            <section className="nl-card flex flex-col gap-2 p-5" aria-label="Balance">
              <span className={capsCls}>Balance</span>
              {balanceRows(doc).map((r) => (
                <div
                  key={r.label}
                  className="flex justify-between gap-2.5 text-[14px]"
                  style={{
                    color: r.tone === 'pos' ? 'var(--posFg)' : r.tone === 'strong' ? 'var(--text)' : 'var(--text2)',
                    fontWeight: r.tone === 'strong' ? 700 : 400,
                  }}
                >
                  <span>{r.label}</span><span className="nl-num tabular-nums">{r.value}</span>
                </div>
              ))}
              <div
                className="mt-1 h-1.5 overflow-hidden rounded-full bg-surface2"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={pct}
                aria-label="Collected"
              >
                <span className="block h-full" style={{ width: `${pct}%`, background: 'var(--pos)' }} />
              </div>
              <span className="text-[12px] text-text3">{isVoid ? 'Void invoices have no balance.' : `${pct}% collected`}</span>
            </section>
          )}

          {isInvoice && !isDraft && (
            <section className="nl-card overflow-hidden" aria-labelledby="inv-payments">
              <div className="flex items-center justify-between px-[18px] py-3.5">
                <h2 id="inv-payments" className="m-0 text-[14px] font-semibold text-text1">Payments</h2>
                {has('recordPayment') && (
                  <button type="button" className="border-0 bg-transparent text-[13px] font-semibold text-brand" onClick={() => setModal('pay')}>+ Record</button>
                )}
              </div>
              {rows.length === 0 && (
                <p className="m-0 border-t border-borderc px-[18px] py-3.5 text-[13px] text-text3">No payments recorded yet.</p>
              )}
              {rows.map((p) => (
                <div key={p.key} className="grid gap-x-2.5 gap-y-0.5 border-t border-borderc px-[18px] py-2.5" style={{ gridTemplateColumns: 'minmax(0,1fr) auto' }}>
                  <span
                    className={`font-semibold ${p.rejected ? 'text-text3' : 'text-text1'}`}
                    style={p.kind === 'credit' ? { color: 'var(--infoFg)' } : undefined}
                  >
                    {p.title}{p.refunded ? ' · refunded' : ''}
                  </span>
                  <span
                    className={`nl-num font-semibold tabular-nums ${p.rejected ? 'line-through' : ''}`}
                    style={{ color: p.kind === 'credit' ? 'var(--infoFg)' : p.refunded || p.rejected ? 'var(--text3)' : 'var(--posFg)' }}
                  >
                    {money(p.amountMinor, cur)}
                  </span>
                  <span className="min-w-0 truncate text-[12px] text-text3">
                    {p.rejected
                      ? `Rejected · ${p.rejected}`
                      : [p.paidOn ? date(p.paidOn) : p.at ? date(new Date(p.at + 330 * 60000).toISOString()) : null, p.reference].filter(Boolean).join(' · ')}
                  </span>
                  <span className="nl-num text-right text-[12px] text-text3">{p.kind === 'payment' ? p.number ?? '' : ''}</span>
                </div>
              ))}
            </section>
          )}

          {has('reminders') && doc.reminders && (
            <NextReminderCard doc={doc as any} settings={settings} shopId={shopId} onChanged={() => load(true)} />
          )}

          {(detail.messages || []).length > 0 && (
            <MessagesCard
              shopId={shopId}
              messages={detail.messages as any}
              onRetried={async (text) => { await load(true); flash(text); }}
              onShareFromDevice={() => setModal('share')}
            />
          )}

          <ActivityTimeline events={detail.events} currency={cur} />
        </div>
      </div>

      {/* Dialogs */}
      {modal === 'pay' && (
        <RecordPaymentModal
          shopId={shopId}
          doc={payDoc}
          onClose={() => setModal(null)}
          onDone={(r) => afterModal(`${money(r.payment.amountMinor, cur)} recorded${r.receipt?.number ? ` · receipt ${r.receipt.number}` : ''}`)}
        />
      )}
      {modal === 'credit' && (
        <CreditNoteModal
          shopId={shopId}
          doc={doc}
          onClose={() => setModal(null)}
          onDone={(r) => afterModal(r.creditNote.number ? `Credit note ${r.creditNote.number} issued` : 'Credit note issued')}
        />
      )}
      {modal === 'void' && (
        <VoidModal shopId={shopId} doc={doc} onClose={() => setModal(null)} onDone={(d) => afterModal(`${d.number ?? (isQuote ? 'Quotation' : 'Invoice')} voided`)} />
      )}
      {modal === 'recurring' && (
        <MakeRecurringModal
          open
          shopId={shopId}
          fromDoc={doc as any}
          onClose={() => setModal(null)}
          onCreated={() => { setModal(null); navigate('/invoices/recurring'); }}
        />
      )}
      <ShareDialog
        key={modal === 'send' ? 'send' : 'share'}
        open={modal === 'share' || modal === 'send'}
        initialTab={modal === 'send' ? 'netlise' : 'device'}
        onClose={() => setModal(null)}
        shopId={shopId}
        docId={doc.docId}
        document={doc}
        onShared={() => load(true)}
      />
      {modal === 'regen' && (
        <Dialog
          title="Regenerate the share link?"
          sub="The old link stops working at once. Anyone who opens it sees a not-found page. Send the new link to the customer yourself."
          onClose={() => setModal(null)}
          busy={busy === 'regen'}
          footer={(
            <>
              <button type="button" className="nl-btn nl-btn--secondary" onClick={() => setModal(null)} disabled={busy === 'regen'}>Cancel</button>
              <button type="button" className="nl-btn nl-btn--primary" onClick={regenerate} disabled={busy === 'regen'}>
                {busy === 'regen' ? 'Regenerating…' : 'Regenerate link'}
              </button>
            </>
          )}
        />
      )}
      {modal && typeof modal === 'object' && (
        <Dialog
          title="New link created"
          sub="The old link is revoked."
          onClose={() => setModal(null)}
          footer={(
            <>
              <button type="button" className="nl-btn nl-btn--secondary" onClick={() => setModal(null)}>Done</button>
              <button type="button" className="nl-btn nl-btn--primary" onClick={() => copyLink(modal.link)}>Copy link</button>
            </>
          )}
        >
          <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-text2">
            Share link
            <input className="nl-input nl-num" readOnly value={modal.link} onFocus={(e) => e.target.select()} />
          </label>
        </Dialog>
      )}
      {modal === 'delete' && (
        <Dialog
          title="Delete this draft?"
          sub="The draft is removed. Drafts never hold a number, so nothing is skipped in your numbering."
          onClose={() => setModal(null)}
          busy={busy === 'delete'}
          footer={(
            <>
              <button type="button" className="nl-btn nl-btn--secondary" onClick={() => setModal(null)} disabled={busy === 'delete'}>Cancel</button>
              <button type="button" className="nl-btn nl-btn--danger" onClick={removeDraft} disabled={busy === 'delete'}>
                {busy === 'delete' ? 'Deleting…' : 'Delete draft'}
              </button>
            </>
          )}
        />
      )}

      {toast && (
        <div
          role="status"
          className="fixed bottom-6 left-1/2 z-[1100] flex -translate-x-1/2 items-center gap-2 rounded-[10px] px-4 py-2.5 text-[14px] font-medium text-white"
          style={{ background: '#131318', boxShadow: 'var(--shadowLg)' }}
        >
          <span aria-hidden style={{ color: '#45D39F' }}>✓</span>{toast}
        </div>
      )}
    </div>
  );
}

/**
 * Messages Netlise sent for this document: channel, masked recipient,
 * template, status, time and credits. Failed or bounced ones can be sent
 * again (new credits); when credits run out, or for a failed WhatsApp or
 * SMS, the owner can share from their own device instead.
 */
function MessagesCard({ shopId, messages, onRetried, onShareFromDevice }: {
  shopId: string;
  messages: MessageRow[];
  onRetried: (text: string) => void | Promise<void>;
  onShareFromDevice: () => void;
}) {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [problem, setProblem] = useState<{ id: string; text: string; noCredits: boolean } | null>(null);
  const list = [...messages].sort((a, b) => messageTime(b) - messageTime(a));

  const retry = async (m: MessageRow) => {
    setBusyId(m.messageId);
    setProblem(null);
    try {
      const res = await InvoiceRepository.retryMessage(shopId, m.messageId);
      const used = res.creditsUsed || 0;
      const name = m.channel === 'EMAIL' ? 'email' : m.channel === 'SMS' ? 'SMS' : 'WhatsApp';
      await onRetried(`Sent again by ${name}${used ? ` · ${creditText(used)} used` : ''}`);
    } catch (e: any) {
      setProblem({ id: m.messageId, ...retryProblem(e) });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section className="nl-card overflow-hidden" aria-labelledby="inv-messages">
      <div className="px-[18px] py-3.5">
        <h2 id="inv-messages" className="m-0 text-[14px] font-semibold text-text1">Messages</h2>
      </div>
      <ul className="m-0 list-none p-0">
        {list.map((m) => {
          const failed = canRetry(m);
          const p = problem?.id === m.messageId ? problem : null;
          return (
            <li key={m.messageId} className="flex flex-col gap-1.5 border-t border-borderc px-[18px] py-3">
              <div className="grid items-center gap-x-2.5" style={{ gridTemplateColumns: '22px minmax(0,1fr) auto' }}>
                <ChannelGlyph glyph={messageTag(m).glyph} size={22} title={m.channel === 'EMAIL' ? 'Email' : m.channel === 'SMS' ? 'SMS' : 'WhatsApp'} />
                <span className="flex min-w-0 flex-col">
                  <span className="nl-num truncate text-[13px] font-semibold text-text1">{m.to || '—'}</span>
                  <span className="truncate text-[12px] text-text3">
                    {[templateLabel(m.templateKey), dateTime(messageTime(m)), creditText(m.creditCost)].join(' · ')}
                  </span>
                </span>
                <DeliveryTag tag={messageTag(m)} />
              </div>
              {failed && m.error && <span className="pl-8 text-[12px]" style={{ color: 'var(--negFg)' }}>{m.error}</span>}
              {p && (
                <span role="alert" className="pl-8 text-[12px] font-semibold" style={{ color: 'var(--negFg)' }}>{p.text}</span>
              )}
              {failed && (
                <div className="flex flex-wrap gap-2 pl-8">
                  <button
                    type="button"
                    className="nl-btn nl-btn--xs nl-btn--secondary"
                    disabled={busyId !== null}
                    onClick={() => retry(m)}
                  >
                    {busyId === m.messageId ? 'Sending…' : 'Retry'}
                  </button>
                  {p?.noCredits && (
                    <button type="button" className="nl-btn nl-btn--xs nl-btn--secondary" onClick={onShareFromDevice}>Share from my device</button>
                  )}
                  {!p?.noCredits && offersDeviceShare(m) && (
                    <button type="button" className="nl-btn nl-btn--xs nl-btn--ghost" onClick={onShareFromDevice}>Share from my WhatsApp</button>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
